import React, { useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { createPortal } from 'react-dom';
import { Button } from '../Button/Button';
import { Toggle } from '../../Objects/Toggle/Toggle';
import uuidv4 from '../../utils/uuidv4';
import { floatGridAreas, gridAreaClass, gridAreaDataAttribute, gridElementClass, gridElementId, } from '../../features/useFloatGrid';
/**
 * Breakpoint above which `.c-action-bar-trigger` is hidden by Bronson CSS.
 */
const ACTION_BAR_DESKTOP_MQ = '(min-width: 960px)';
/**
 * Bronson ActionBar component.
 *
 * @see https://bronson.vwfs.io/default/components/detail/bronson-action-bar.html
 *
 * @constructor
 */
export function ActionBar({ className, labelSecondaryActions = 'Secondary Actions', labelShowLess = 'Show less', labelShowMore = 'Show more', primaryAction, secondaryActions, testId, ...otherProps }) {
    const [portalTarget, setPortalTarget] = useState(null);
    const [isDesktop, setIsDesktop] = useState(() => window.matchMedia(ACTION_BAR_DESKTOP_MQ).matches);
    const classNameList = classNames('c-action-bar', className).trim();
    const id = useMemo(() => uuidv4('action-bar-'), []);
    const toggleId = `${id}toggle`;
    const panelId = `${id}panel`;
    const labelId = `${id}label`;
    const panelRef = useRef(null);
    useEffect(() => {
        const mq = window.matchMedia(ACTION_BAR_DESKTOP_MQ);
        const handler = (e) => setIsDesktop(e.matches);
        mq.addEventListener('change', handler);
        return () => mq.removeEventListener('change', handler);
    }, []);
    useEffect(() => {
        let grid = document.querySelector(`#${gridElementId}`);
        if (!grid) {
            grid = document.createElement('div');
            grid.id = gridElementId;
            grid.className = gridElementClass;
            floatGridAreas.forEach((area) => {
                grid.insertAdjacentHTML('beforeend', `<div class="${gridAreaClass}" ${gridAreaDataAttribute}="${area}"></div>`);
            });
            document.body.insertBefore(grid, document.body.firstElementChild);
        }
        setPortalTarget(grid.querySelector(`[${gridAreaDataAttribute}=bottom]`));
    }, []);
    const actionBar = (React.createElement("div", { className: classNameList, role: "toolbar", "data-testid": testId, ...otherProps },
        React.createElement("div", { className: "c-action-bar-trigger__wrapper" },
            primaryAction && (React.createElement(Button, { className: "c-action-bar__button", element: primaryAction.href ? 'a' : 'button', href: primaryAction.href, icon: primaryAction.icon, onClick: primaryAction.onClick, type: primaryAction.href ? undefined : 'button' }, primaryAction.label)),
            React.createElement(Toggle, { className: "c-action-bar-trigger", defaultHidden: true, hidden: isDesktop ? false : undefined, id: toggleId, labelHidden: labelShowMore, labelUnhidden: labelShowLess, link: true, small: true, simple: true, icon: "semantic-expand", targetId: panelId, targetRef: panelRef, type: "button" })),
        React.createElement("span", { id: labelId, className: "u-visually-hidden" }, labelSecondaryActions),
        React.createElement("div", { id: panelId, className: "c-action-bar__panel", role: "group", "aria-labelledby": toggleId, ref: panelRef },
            React.createElement("ul", { className: "c-action-bar__panel__list", "aria-labelledby": labelId }, secondaryActions?.map((item, index) => (
            // eslint-disable-next-line react/no-array-index-key
            React.createElement("li", { key: index, className: "c-action-bar__item" },
                React.createElement(Button, { ariaLabel: item.ariaLabel, element: item.href ? 'a' : 'button', href: item.href, icon: item.icon, link: true, onClick: item.onClick, simple: true, small: true, type: item.href ? undefined : 'button' }, item.label))))))));
    return portalTarget ? createPortal(actionBar, portalTarget) : actionBar;
}
